import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { cancelSupply } from '../../api/store/cancelSupply';
import { Cafe, Ingredient, Supply } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormSelect from '../../components/forms/FormSelect';
import FormSupplyComponentsTable from '../../components/forms/FormSupplyComponentsTable';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { createBatch } from '../../utils/api';
import currency from '../../utils/currency';
import { getPriceByUnit } from '../../utils/getPriceByUnit';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  supply?: DbDocument<Supply> | null;
}

const SupplyEditModal = (props: Props) => {
  const { onClose, supply, onSubmit } = props;
  const { products, productsMap, worker, walletsMap, storesMap } = useCafe();
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();

  const form = useForm<Supply>({
    defaultValues: {
      provider: supply?.provider || '',
      components: supply?.components || [],
    },
  });
  const provider = form.watch('provider');
  const components = form.watch('components');

  const disabled = supply?.status === 'closed' || supply?.status === 'deleted';

  const providers = useMemo(() => Array.from(new Set(products.map((c) => c.provider))).filter((p) => !!p), [products]);

  const options = useMemo(
    () =>
      products.map<Ingredient>((p) => ({
        id: p.id,
        unit: p.unit,
        qpp: p.qpp,
        imageUrl: p.imageUrl,
        name: p.name,
        category: p.category,
        type: 'product',
      })),
    [products]
  );

  const total = components.reduce(
    (acc, { id, qnt, price }) => acc + getPriceByUnit(price || 0, productsMap[id]) * qnt,
    0
  );

  const handleRemove = useCallback(async () => {
    if (!supply) {
      return;
    }

    const batch = createBatch();
    batch.update<Supply>(`cafes/${cafeSlug}/supply/${supply.id}`, {
      status: 'deleted',
    });

    await batch.commit();
    onClose();
  }, [cafeSlug, supply, onClose]);

  const saveChanges = useCallback(
    async (values: Supply) => {
      const components = values.components.filter((c) => c.qnt);
      const batch = createBatch();

      if (!supply) {
        const id = uniqid();
        const number = (cafe?.lastSupplyNumber || 1000) + 1;

        batch.set<Supply>(`cafes/${cafeSlug}/supply/${id}`, {
          time: new Date().getTime(),
          number,
          status: 'active',
          provider: values.provider,
          components,
          storeId: null,
          walletId: null,
          totalCost: null,
        });

        batch.update<Cafe>(`cafes/${cafeSlug}`, { lastSupplyNumber: number });
      } else {
        batch.update<Supply>(`cafes/${cafeSlug}/supply/${supply.id}`, {
          provider: values.provider,
          components,
        });
      }

      await batch.commit();

      onSubmit();
      onClose();
    },
    [cafe?.lastSupplyNumber, cafeSlug, onClose, onSubmit, supply]
  );

  const handleCancelSupply = useCallback(async () => {
    if (supply && worker) {
      await cancelSupply({ supply, cafeSlug, worker, productsMap, walletsMap });
    }
    onSubmit();
    onClose();
  }, [cafeSlug, onClose, onSubmit, productsMap, supply, walletsMap, worker]);

  useEffect(() => {
    if (!supply && provider) {
      form.setValue(
        'components',
        products
          .filter((p) => p.provider === provider)
          .map((p) => ({
            id: p.id,
            qnt: 0,
            price: p.price || null,
          }))
      );
    }
  }, [form, products, provider, supply]);

  return (
    <EditModal
      open
      title={t.supply[lang]}
      form={form}
      onClose={onClose}
      submitButton={supply ? t.save_changes[lang] : t.create_supply[lang]}
      submitButtonProps={{ disabled: !form.formState.isDirty || components.length === 0 }}
      // eslint-disable-next-line no-nested-ternary
      secondaryButton={supply ? (supply.status === 'closed' ? t.cancel[lang] : t.remove[lang]) : undefined}
      secondaryButtonProps={{ disabled: supply?.status === 'deleted' || !worker?.credentials.edit_supplies }}
      onSecondaryButtonClick={supply?.status === 'closed' ? handleCancelSupply : handleRemove}
      onSubmit={saveChanges}
      size="md"
    >
      <Box sx={{ mb: 3, maxWidth: 300 }}>
        <FormSelect
          disabled={disabled}
          name="provider"
          color="secondary"
          label={t.provider[lang]}
          options={providers}
        />
      </Box>

      <FormSupplyComponentsTable disabled={disabled} form={form} options={options} />

      <Typography data-testid="total" variant="h6" textAlign="right">
        {t.total[lang]}: {currency(total, cafe?.currency)}
      </Typography>
    </EditModal>
  );
};

export default SupplyEditModal;
